import { Controller } from "@hotwired/stimulus"

/**
 * Collapsible Controller - Progressive disclosure for complex UI sections.
 * 
 * Usage:
 *   <div data-controller="collapsible" data-collapsible-open-value="true" data-collapsible-remember-value="layers-section">
 *     <button data-action="click->collapsible#toggle" data-collapsible-target="header" class="collapsible-header">
 *       <span class="collapsible-title">Section Title</span>
 *       <svg data-collapsible-target="icon" class="collapsible-icon">...</svg>
 *     </button>
 *     <div data-collapsible-target="content" class="collapsible-content">
 *       <div class="collapsible-body">Content here</div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["header", "content", "icon"]
  static values = {
    open: { type: Boolean, default: true },
    remember: { type: String, default: "" },
    animationDuration: { type: Number, default: 300 }
  }

  connect() {
    this.restoreStateFromSessionStorage()
    this.applyState(false)
  }

  restoreStateFromSessionStorage() {
    if (!this.rememberValue) return
    
    const savedState = sessionStorage.getItem(`collapsible-${this.rememberValue}`)
    if (savedState !== null) {
      this.openValue = savedState === "true"
    }
  }

  toggle(event) {
    event?.preventDefault()
    this.openValue = !this.openValue
    this.applyState(true)
    this.persistState()
  }

  open(event) {
    event?.preventDefault()
    if (this.openValue) return
    
    this.openValue = true
    this.applyState(true)
    this.persistState()
  }

  close(event) {
    event?.preventDefault()
    if (!this.openValue) return
    
    this.openValue = false
    this.applyState(true)
    this.persistState()
  }

  applyState(animate = true) {
    if (this.openValue) {
      this.expandContent(animate)
    } else {
      this.collapseContent(animate)
    }
    this.updateIconState()
    this.updateAriaState()
  }

  expandContent(animate) {
    const content = this.contentTarget
    content.setAttribute("data-state", "open")
    
    if (!animate) {
      content.style.maxHeight = null
      return
    }

    content.style.maxHeight = "0px"
    content.offsetHeight
    content.style.maxHeight = `${content.scrollHeight}px`
    
    setTimeout(() => {
      content.style.maxHeight = null
    }, this.animationDurationValue)
  }

  collapseContent(animate) {
    const content = this.contentTarget
    content.setAttribute("data-state", "closed")
    
    if (!animate) {
      content.style.maxHeight = "0px"
      return
    }

    content.style.maxHeight = `${content.scrollHeight}px`
    content.offsetHeight
    content.style.maxHeight = "0px"
  }

  updateIconState() {
    if (!this.hasIconTarget) return
    this.iconTarget.classList.toggle("collapsible-icon-open", this.openValue)
  }

  updateAriaState() {
    this.headerTarget?.setAttribute("aria-expanded", this.openValue.toString())
  }

  persistState() {
    if (!this.rememberValue) return
    sessionStorage.setItem(`collapsible-${this.rememberValue}`, this.openValue.toString())
  }

  keydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.toggle()
    }
  }
}
